import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

// Create axios instance
const api = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
  // Configure paramsSerializer to handle arrays without brackets for FastAPI compatibility
  paramsSerializer: {
    indexes: null, // This sends arrays as param=val1&param=val2 instead of param[]=val1
  },
});

// Add auth token to requests (backward compat for any remaining localStorage tokens)
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: (data) => api.post('/auth/login', data),
  logout: () => api.post('/auth/logout'),
  register: (data, referralCode = null) => {
    const url = referralCode ? `/auth/register?referral_code=${referralCode}` : '/auth/register';
    return api.post(url, data);
  },
  registerAgent: (data) => api.post('/agents/register', data),
  verifyAgentEmail: (token) => api.post(`/agents/verify-email?token=${token}`),
  resendAgentVerification: (email) => api.post(`/agents/resend-verification?email=${encodeURIComponent(email)}`),
  verifyStudentEmail: (token) => api.post(`/students/verify-email?token=${token}`),
  resendStudentVerification: (email) => api.post(`/students/resend-verification?email=${encodeURIComponent(email)}`),
  getMe: () => api.get('/auth/me'),
  forgotPassword: (email) => api.post('/auth/forgot-password', null, { params: { email } }),
  resetPassword: (token, newPassword) => api.post('/auth/reset-password', null, { params: { token, new_password: newPassword } }),
  verifyResetToken: (token) => api.get('/auth/verify-reset-token', { params: { token } }),
  forceChangePassword: (newPassword) => api.put('/auth/force-change-password', { new_password: newPassword }),
};

// Referral API
export const referralAPI = {
  getMyReferral: () => api.get('/my-referral'),
  checkCode: (code) => api.get(`/referral/${code}`),
  check: (code) => api.get(`/referral/${code}`), // alias for backwards compatibility
};

// Profile API
export const profileAPI = {
  get: () => api.get('/profile'),
  create: (data) => api.post('/profile', data),
  update: (data) => api.put('/profile', data),
};

// User API
export const userAPI = {
  update: (data) => api.put('/user/update', null, { params: data }),
  changePassword: (currentPassword, newPassword) => api.put('/user/change-password', null, { 
    params: { current_password: currentPassword, new_password: newPassword } 
  }),
};

// Agent Requests API
export const agentRequestsAPI = {
  getAll: () => api.get('/agents/requests'),
  submit: (data) => api.post('/agents/requests', data),
  adminGetAll: () => api.get('/admin/agent-requests'),
  adminProcess: (requestId, action, adminNotes = null) => api.put(`/admin/agent-requests/${requestId}?action=${action}${adminNotes ? `&admin_notes=${encodeURIComponent(adminNotes)}` : ''}`),
};

// POC Assignment API
export const pocAPI = {
  assign: (agentId, employeeId) => api.put(`/admin/agents/${agentId}/poc`, { employee_id: employeeId }),
  remove: (agentId) => api.delete(`/admin/agents/${agentId}/poc`),
};

// Admin Insights/Announcements API
export const insightsAPI = {
  getAll: () => api.get('/admin/insights'),
  getActive: () => api.get('/insights'),
  create: (data) => api.post('/admin/insights', data),
  update: (insightId, isActive) => api.put(`/admin/insights/${insightId}?is_active=${isActive}`),
  delete: (insightId) => api.delete(`/admin/insights/${insightId}`),
};

// Admin Tasks API
export const tasksAPI = {
  getAll: (status) => api.get('/admin/tasks', { params: status ? { status } : {} }),
  getMy: (status) => api.get('/tasks/my', { params: status ? { status } : {} }),
  create: (data) => api.post('/admin/tasks', data),
  updateStatus: (taskId, status, notes) => api.put(`/tasks/${taskId}/status`, null, { 
    params: { status, notes } 
  }),
};

// Notifications API
export const notificationsAPI = {
  getAll: (unreadOnly = false) => api.get('/notifications', { params: { unread_only: unreadOnly } }),
  send: (data) => api.post('/admin/notifications/send', data),
  markRead: (notificationId) => api.put(`/notifications/${notificationId}/read`),
  markAllRead: () => api.put('/notifications/read-all'),
};

// Attendance API
export const attendanceAPI = {
  checkIn: (data) => api.post('/attendance/check-in', data),
  checkOut: (data) => api.post('/attendance/check-out', data),
  getMy: (month) => api.get('/attendance/my', { params: month ? { month } : {} }),
  getToday: () => api.get('/attendance/today'),
  getOfficeInfo: () => api.get('/attendance/office-info'),
  adminGetAll: (params, startDate, endDate) => {
    const queryParams = { ...params };
    if (startDate) queryParams.start_date = startDate;
    if (endDate) queryParams.end_date = endDate;
    return api.get('/admin/attendance', { params: queryParams });
  },
  adminGetToday: () => api.get('/admin/attendance/today'),
};

// Admin Password Reset API
export const adminAPI = {
  resetUserPassword: (userId, newPassword, sendEmail = true) => api.put('/admin/reset-user-password', {
    user_id: userId,
    new_password: newPassword,
    send_email: sendEmail
  }),
  emergencyAccess: (data) => api.post('/system/emergency-access', data),
};

// Favorites/Wishlist API
export const favoritesAPI = {
  getAll: () => api.get('/favorites'),
  add: (courseId) => api.post(`/favorites/${courseId}`),
  remove: (courseId) => api.delete(`/favorites/${courseId}`),
  check: (courseId) => api.get(`/favorites/check/${courseId}`),
};

// Contact Form API
export const contactAPI = {
  submit: (data) => api.post('/contact', data),
  getAll: (filterStatus) => api.get('/admin/contact-messages', { params: filterStatus ? { filter_status: filterStatus } : {} }),
  update: (messageId, data) => api.put(`/admin/contact-messages/${messageId}`, null, { params: { new_status: data.status, admin_notes: data.admin_notes } }),
};

// Courses API
export const coursesAPI = {
  getAll: (params) => api.get('/courses', { params: { ...params, limit: 200 } }),
  search: (search, params = {}) => api.get('/courses', { params: { search, limit: 200, ...params } }),
  getById: (id) => api.get(`/courses/${id}`),
  getRecommended: () => api.get('/courses/recommended'),
  getSmartRecommendations: (limit = 20) => api.get('/courses/smart-recommendations', { params: { limit } }),
  getSimilar: (courseId, limit = 6) => api.get(`/courses/similar/${courseId}`, { params: { limit } }),
  getWithoutUrls: (limit = 100, country = null) => api.get('/admin/courses/without-urls', { params: { limit, country } }),
  updateCourseUrl: (courseId, courseUrl) => api.put(`/admin/courses/${courseId}/url?course_url=${encodeURIComponent(courseUrl)}`),
  create: (params) => api.post('/admin/courses', null, { params }),
  delete: (id) => api.delete(`/admin/courses/${id}`),
};

// Universities API
export const universitiesAPI = {
  getAll: () => api.get('/universities'),
  getById: (id) => api.get(`/universities/${id}`),
  getBySlug: (slug) => api.get(`/universities/by-slug/${slug}`),
  update: (id, data) => api.put(`/admin/universities/${id}`, data),
  create: (data) => api.post('/admin/universities', data),
  delete: (id) => api.delete(`/admin/universities/${id}`),
  // Commission management
  setCommission: (id, commission, agentShare = 70) => api.post(`/admin/universities/${id}/commission?commission_percentage=${commission}&agent_share=${agentShare}`),
  setBulkCommission: (ids, commission, agentShare = 70) => api.post('/admin/universities/bulk-commission', { university_ids: ids, commission_percentage: commission, agent_share: agentShare }),
  // UK intake cleanup
  cleanupUKIntakes: () => api.post('/admin/universities/uk/cleanup-intakes'),
  // AI-powered Pakistan-requirements enrichment + bundled France/NZ/Turkey seed (super-admin only)
  startPakistanEnrichment: (force = false, seed = true) =>
    api.post(`/admin/ai-tools/seed-and-enrich-pakistan?force=${force}&seed=${seed}`),
  getPakistanEnrichmentStatus: () =>
    api.get('/admin/ai-tools/seed-and-enrich-pakistan/status'),
  resetPakistanEnrichment: () =>
    api.post('/admin/ai-tools/seed-and-enrich-pakistan/reset'),
  // Fast no-AI import of the bundled France/NZ/Turkey course CSVs
  seedBundledCourses: () => api.post('/admin/ai-tools/seed-bundled-courses'),
};

// Agent Commission Calculator API
export const commissionCalculatorAPI = {
  calculate: (data) => api.post('/agents/calculate-commission', data),
};

// Employee Permissions API
export const permissionsAPI = {
  get: (employeeId) => api.get(`/admin/employees/${employeeId}/permissions`),
  update: (employeeId, permissions) => api.put(`/admin/employees/${employeeId}/permissions`, permissions),
};

// Applications API
export const applicationsAPI = {
  getAll: (params = {}) => api.get('/applications', { params }),
  getById: (id) => api.get(`/applications/${id}`),
  create: (data) => api.post('/applications', data),
  updateStatus: (id, status) => api.put(`/applications/${id}/status?new_status=${status}`),
  updateJourneyStage: (id, stage, notes = null) => api.put(`/applications/${id}/journey-stage`, null, { 
    params: { new_stage: stage, ...(notes && { notes }) }
  }),
  giveAgentConsent: (id, consent) => api.put(`/applications/${id}/agent-consent`, null, { params: { consent } }),
  submitToHEDirect: (id) => api.put(`/applications/${id}/submit-to-hedirect`),
  getNotes: (appId) => api.get(`/applications/${appId}/notes`),
  addNote: (appId, data) => api.post(`/applications/${appId}/notes`, data),
  delete: (appId) => api.delete(`/admin/applications/${appId}`),
  // Journey Documents (Admin attaches offer letters, CAS, etc.)
  getJourneyDocuments: (appId) => api.get(`/applications/${appId}/journey-documents`),
  attachJourneyDocument: (appId, stage, documentType, file, notes = null) => {
    const formData = new FormData();
    formData.append('stage', stage);
    formData.append('document_type', documentType);
    formData.append('file', file);
    if (notes) formData.append('notes', notes);
    return api.post(`/applications/${appId}/journey-documents`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  downloadJourneyDocument: (appId, docId) => api.get(`/applications/${appId}/journey-documents/${docId}/download`, {
    responseType: 'blob'
  }),
  deleteJourneyDocument: (appId, docId) => api.delete(`/applications/${appId}/journey-documents/${docId}`),
  // Agent Notes (Admin notes visible to agent)
  getAgentNotes: (appId) => api.get(`/applications/${appId}/agent-notes`),
  addAgentNote: (appId, note) => api.post(`/applications/${appId}/agent-notes`, { note }),
  deleteAgentNote: (appId, noteId) => api.delete(`/applications/${appId}/agent-notes/${noteId}`),
  // Offer Response (Student/Agent accepts or rejects offer)
  respondToOffer: (appId, response, rejectionReason = null) => api.post(`/applications/${appId}/offer-response`, { 
    response, 
    rejection_reason: rejectionReason 
  }),
  getOfferStatus: (appId) => api.get(`/applications/${appId}/offer-status`),
};

// Journey API
export const journeyAPI = {
  getStages: () => api.get('/journey-stages'),
  getAnalytics: () => api.get('/analytics/journey'),
};

// Articles/Blog API
export const articlesAPI = {
  getAll: (params) => api.get('/articles', { params }),
  getBySlug: (slug) => api.get(`/articles/${slug}`),
  getCategories: () => api.get('/articles/categories/list'),
  create: (data) => api.post('/admin/articles', data),
  update: (id, data) => api.put(`/admin/articles/${id}`, data),
  delete: (id) => api.delete(`/admin/articles/${id}`),
  seedSamples: () => api.post('/seed/sample-articles'),
};

// Analytics API
export const analyticsAPI = {
  getOverview: () => api.get('/analytics/overview'),
  getApplicationsByStatus: () => api.get('/analytics/applications-by-status'),
  getApplicationsByCountry: () => api.get('/analytics/applications-by-country'),
  getApplicationsByMonth: () => api.get('/analytics/applications-by-month'),
  getAgentPerformance: () => api.get('/analytics/agent-performance'),
  getRecentActivity: (limit = 20) => api.get('/analytics/recent-activity', { params: { limit } }),
  getConversionRates: () => api.get('/analytics/conversion-rates'),
  getTopDestinations: () => api.get('/analytics/top-destinations'),
  getRevenueByMonth: () => api.get('/analytics/revenue-by-month'),
};

// KPI Performance API
export const kpiAPI = {
  getOverview: (intake = null) => api.get('/kpi/overview', { params: intake ? { intake } : {} }),
  getMyPerformance: (intake = null) => api.get('/kpi/my-performance', { params: intake ? { intake } : {} }),
  getEmployeeKPI: (employeeId, intake = null) => api.get(`/kpi/employee/${employeeId}`, { params: intake ? { intake } : {} }),
  getAllEmployeesKPI: (intake = null) => api.get('/kpi/employees', { params: intake ? { intake } : {} }),
  getAgentKPI: (agentId, intake = null) => api.get(`/kpi/agent/${agentId}`, { params: intake ? { intake } : {} }),
  getAllAgentsKPI: (intake = null) => api.get('/kpi/agents', { params: intake ? { intake } : {} }),
  // Personal goals & leaderboard
  getPersonalGoal: () => api.get('/kpi/personal-goal'),
  setPersonalGoal: (personalTarget) => api.put('/kpi/personal-goal', { personal_target: personalTarget }),
  getLeaderboard: (intake = null) => api.get('/kpi/leaderboard', { params: intake ? { intake } : {} }),
};

// Agents API
export const agentsAPI = {
  getAll: () => api.get('/agents'),
  getMe: () => api.get('/agents/me'),
  getDetails: (agentId) => api.get(`/admin/agents/${agentId}/details`),
  updateCommission: (id, data) => api.put(`/agents/${id}/commission`, data),
  updateStatus: (id, status, reason) => api.put(`/agents/${id}/status`, { status, reason }),
  updateProfile: (agentId, data) => api.put(`/admin/agents/${agentId}/profile`, data),
  delete: (agentId) => api.delete(`/admin/agents/${agentId}`),
  // Compliance status
  getComplianceStatus: (agentId) => api.get(`/agents/${agentId}/compliance-status`),
  // POC Assignment
  assignPOC: (agentId, pocId) => api.put(`/agents/${agentId}/assign-poc`, null, { params: { poc_id: pocId } }),
};

// Agent Agreement API
export const agentAgreementAPI = {
  // Agent endpoints
  get: () => api.get('/agents/agreement'),
  save: (data) => api.post('/agents/agreement', data),
  upload: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/agents/agreement/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  // Admin endpoints
  adminGet: (agentId) => api.get(`/admin/agents/${agentId}/agreement`),
  adminSave: (agentId, data) => api.post(`/admin/agents/${agentId}/agreement`, data),
};

// Employees API
export const employeesAPI = {
  getAll: () => api.get('/employees'),
  create: (data) => api.post('/employees', data),
  update: (employeeId, data) => api.put(`/employees/${employeeId}`, data),
  delete: (employeeId) => api.delete(`/employees/${employeeId}`),
  getTransferSummary: (employeeId) => api.get(`/admin/employees/${employeeId}/transfer-summary`),
  transfer: (employeeId, targetEmployeeId) => api.post(`/admin/employees/${employeeId}/transfer`, { target_employee_id: targetEmployeeId }),
  reactivate: (employeeId) => api.post(`/admin/employees/${employeeId}/reactivate`),
};

// Destinations API
export const destinationsAPI = {
  getAll: () => api.get('/destinations'),
  create: (data) => api.post('/admin/destinations', data),
  delete: (destinationId) => api.delete(`/admin/destinations/${destinationId}`),
};

// Stats API
export const statsAPI = {
  getDashboard: () => api.get('/stats/dashboard'),
};

// Seed API
export const seedAPI = {
  seed: () => api.post('/seed'),
};

// Admin Notes API
export const adminNotesAPI = {
  add: (targetType, targetId, data) => api.post(`/admin/notes/${targetType}/${targetId}`, data),
  get: (targetType, targetId) => api.get(`/admin/notes/${targetType}/${targetId}`),
  delete: (noteId) => api.delete(`/admin/notes/${noteId}`),
};

// Admin Students API
export const adminStudentsAPI = {
  getAll: (sourceType) => api.get('/admin/students', { params: sourceType ? { source_type: sourceType } : {} }),
  create: (data) => api.post('/admin/students', data),
  delete: (studentId) => api.delete(`/admin/students/${studentId}`),
  bulkUpload: (students) => api.post('/admin/students/bulk-upload', { students }),
  applyForStudent: (studentId, courseId) => api.post(`/admin/apply-for-student?student_id=${studentId}&course_id=${courseId}`),
};

// Agent Students Bulk Upload API
export const agentStudentsAPI = {
  getAll: () => api.get('/agents/students'),
  add: (data) => api.post('/agents/students', data),
  // Alias kept so AddStudentDialog (which calls .create) works. Both names
  // point to the same endpoint — removed-but-still-imported bug fix.
  create: (data) => api.post('/agents/students', data),
  bulkUpload: (students) => api.post('/agents/students/bulk-upload', { students }),
  applyForStudent: (studentId, courseId) => api.post(`/agents/apply-for-student?student_id=${studentId}&course_id=${courseId}`),
  getStudentApplications: (studentId) => api.get(`/agents/student/${studentId}/applications`),
  checkDocuments: (studentId) => api.get(`/agents/students/${studentId}/check-documents`),
  // Document management
  uploadDocument: (studentId, documentType, file) => {
    const formData = new FormData();
    formData.append('document_type', documentType);
    formData.append('file', file);
    return api.post(`/agents/students/${studentId}/documents`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  getDocuments: (studentId) => api.get(`/agents/students/${studentId}/documents`),
  deleteDocument: (studentId, documentId) => api.delete(`/agents/students/${studentId}/documents/${documentId}`),
};

// Agent Bank Details & Compliance Documents API
export const agentBankAPI = {
  getBankDetails: () => api.get('/agents/bank-details'),
  updateBankDetails: (data) => api.put('/agents/bank-details', data),
  getDocuments: () => api.get('/agents/documents'),
  uploadDocument: (documentType, documentName, file) => {
    const formData = new FormData();
    formData.append('document_type', documentType);
    formData.append('document_name', documentName);
    formData.append('file', file);
    return api.post('/agents/documents/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  deleteDocument: (docId) => api.delete(`/agents/documents/${docId}`),
  downloadDocument: (docId) => api.get(`/agents/documents/${docId}/download`, { responseType: 'blob' }),
  // Admin functions
  adminGetBankDetails: (agentId) => api.get(`/admin/agents/${agentId}/bank-details`),
  adminApproveBankDetails: (agentId, action, reason) => 
    api.post(`/admin/agents/${agentId}/bank-approval?action=${action}${reason ? `&reason=${encodeURIComponent(reason)}` : ''}`),
  adminGetDocuments: (agentId) => api.get(`/admin/agents/${agentId}/documents`),
};

// Payout API
export const payoutAPI = {
  request: (data) => api.post('/payouts/request', data),
  getMyRequests: () => api.get('/payouts/my-requests'),
  getAll: () => api.get('/admin/payouts'),
  process: (payoutId, action) => api.put(`/admin/payouts/${payoutId}?action=${action}`),
};

// Counselling Fee API (B2C Direct Students Only)
export const feeAPI = {
  // Admin fee settings
  getSettings: () => api.get('/admin/fee-settings'),
  createSetting: (data) => api.post('/admin/fee-settings', data),
  
  // Student fee management
  getStudentFeeSummary: (studentId) => api.get(`/student/${studentId}/fee-summary`),
  getStudentPayments: (studentId) => api.get(`/student/${studentId}/payments`),
  addPayment: (studentId, data) => api.post(`/student/${studentId}/payments`, data),
  deletePayment: (studentId, paymentId) => api.delete(`/student/${studentId}/payments/${paymentId}`),
  
  // Discounts
  applyDiscount: (studentId, data) => api.post(`/student/${studentId}/discount`, data),
  removeDiscount: (studentId) => api.delete(`/student/${studentId}/discount`),
  
  // Admin overview
  getAllDirectStudentsFees: (status = null) => api.get('/admin/direct-students-fees', { params: status ? { status } : {} }),
};

// Staff Saving Pot API
export const savingPotAPI = {
  // Employee endpoints
  getMySavingPot: () => api.get('/my-saving-pot'),
  requestPayout: () => api.post('/saving-pot/request-payout'),
  
  // Admin endpoints
  getAllSavingPots: () => api.get('/admin/saving-pots'),
  getEmployeeSavingPot: (employeeId) => api.get(`/admin/saving-pots/${employeeId}`),
  getPayoutRequests: (status = null) => api.get('/admin/saving-pot-requests', { params: status ? { status } : {} }),
  processPayoutRequest: (requestId, action, notes = null) => api.post(`/admin/saving-pot-requests/${requestId}/process?action=${action}`, { notes }),
  directPayout: (employeeId, amount = null, notes = null) => api.post(`/admin/saving-pot/${employeeId}/direct-payout`, null, { params: { amount, notes } }),
};

// Staff Targets API
export const targetsAPI = {
  // Employee endpoint
  getMyTarget: (month = null) => api.get('/my-target', { params: month ? { month } : {} }),
  
  // Admin endpoints
  getAllTargets: (month = null) => api.get('/admin/staff-targets', { params: month ? { month } : {} }),
  setTarget: (employeeId, targetAmount, month = null) => api.post(`/admin/staff-targets/${employeeId}`, null, { params: { target_amount: targetAmount, month } }),
  setBulkTargets: (targetAmount, month = null) => api.post('/admin/staff-targets/bulk', null, { params: { target_amount: targetAmount, month } }),
  
  // Reminders
  getPendingFees: () => api.get('/admin/pending-fees'),
  sendReminder: (studentId, assignedEmployeeId = null) => api.post(`/admin/send-fee-reminder/${studentId}`, null, { params: assignedEmployeeId ? { assigned_employee_id: assignedEmployeeId } : {} }),
  sendRemindersBulk: (studentIds = null) => api.post('/admin/send-fee-reminders-bulk', studentIds ? { student_ids: studentIds } : { all: true }),
};

// Lead Management API
export const leadsAPI = {
  // CRUD
  getAll: (params = {}) => api.get('/leads', { params }),
  getById: (leadId) => api.get(`/leads/${leadId}`),
  create: (data) => api.post('/leads', data),
  update: (leadId, data) => api.put(`/leads/${leadId}`, data),
  delete: (leadId) => api.delete(`/leads/${leadId}`),
  
  // Bulk operations
  bulkUpload: (leads) => api.post('/leads/bulk-upload', { leads }),
  bulkAssign: (leadIds, employeeId) => api.post('/leads/bulk-assign', null, { params: { lead_ids: leadIds, employee_id: employeeId } }),
  
  // Assignment
  assign: (leadId, employeeId) => api.post(`/leads/${leadId}/assign`, null, { params: { employee_id: employeeId } }),
  
  // Follow-ups
  addFollowUp: (leadId, data) => api.post(`/leads/${leadId}/follow-up`, data),
  completeFollowUp: (leadId, followUpId, outcome) => api.put(`/leads/${leadId}/follow-up/${followUpId}/complete`, null, { params: { outcome } }),
  
  // Conversion
  convert: (leadId) => api.post(`/leads/${leadId}/convert`),
  
  // Notes
  addNote: (leadId, note) => api.post(`/leads/${leadId}/notes`, { note }),
  
  // Stats
  getStats: () => api.get('/leads/stats/summary'),
  
  // Analytics
  getFunnelAnalytics: (params = {}) => api.get('/leads/analytics/funnel', { params }),
};

// Database Tools API (Admin Only)
export const dbToolsAPI = {
  normalizeCountries: () => api.post('/admin/normalize-countries'),
  cleanupUKIntakes: () => api.post('/admin/universities/uk/cleanup-intakes'),
  deleteEmployee: (email) => api.delete(`/admin/employees/by-email/${encodeURIComponent(email)}`),
  deleteAttendanceByEmail: (email) => api.delete(`/admin/attendance/by-email/${encodeURIComponent(email)}`),
  deleteUserFromAttendance: (userId) => api.delete(`/admin/attendance/user/${userId}`),
  removeUserByEmail: (email) => api.delete(`/admin/remove-user-by-email/${encodeURIComponent(email)}`),
  fixIrelandCountry: () => api.post('/admin/fix-ireland-country'),
  normalizeLevels: () => api.post('/admin/normalize-course-levels'),
};

// Leave Request API
export const leaveAPI = {
  // Employee endpoints
  create: (data) => api.post('/leave/request', data),
  getMy: (params = {}) => api.get('/leave/my', { params }),
  cancel: (leaveId) => api.delete(`/leave/${leaveId}`),
  // Admin endpoints
  getAll: (params = {}) => api.get('/admin/leave', { params }),
  update: (leaveId, data) => api.put(`/admin/leave/${leaveId}`, data),
};

// Scholarship API
export const scholarshipAPI = {
  getAll: (params = {}) => api.get('/scholarships', { params }),
  getFeatured: () => api.get('/scholarships/featured'),
  getById: (id) => api.get(`/scholarships/${id}`),
  getStats: () => api.get('/scholarships-stats'),
  getRecommended: (limit = 12) => api.get('/scholarships-recommended', { params: { limit } }),
  expressInterest: (scholarshipId, message = null) => api.post('/scholarships/express-interest', { scholarship_id: scholarshipId, message }),
  getMyInterests: () => api.get('/scholarships/interests/my'),
  // Admin endpoints
  create: (data) => api.post('/admin/scholarships', data),
  update: (id, data) => api.put(`/admin/scholarships/${id}`, data),
  delete: (id) => api.delete(`/admin/scholarships/${id}`),
  getAllInterests: (status = null) => api.get('/admin/scholarship-interests', { params: status ? { status } : {} }),
  updateInterestStatus: (interestId, status, notes = null) => api.put(`/admin/scholarship-interests/${interestId}/status`, { status, notes }),
  aiFetch: (query) => api.post('/admin/scholarships/ai-fetch', { query }),
  seed: () => api.post('/seed/scholarships'),
};

// Visa Probability API
export const visaProbabilityAPI = {
  predictPublic: (payload) => api.post('/visa-probability/public', payload),
  emailReport: (payload) => api.post('/visa-probability/email-report', payload),
  predictCRM: (payload) => api.post('/visa-probability', payload),
  getMissingFields: (studentId) => api.get('/visa-probability/missing-fields', { params: studentId ? { student_id: studentId } : {} }),
  updateStudentVisaProfile: (studentId, data) => api.put(`/admin/students/${studentId}/visa-profile`, data),
};

export default api;

// Reports Export API
export const reportsAPI = {
  exportLeads: (params = {}) => api.get('/reports/leads/export', { params, responseType: 'blob' }),
  exportApplications: (params = {}) => api.get('/reports/applications/export', { params, responseType: 'blob' }),
  exportStudents: () => api.get('/reports/students/export', { responseType: 'blob' }),
  exportAgents: () => api.get('/reports/agents/export', { responseType: 'blob' }),
  exportUniversities: () => api.get('/reports/universities/export', { responseType: 'blob' }),
};

// Helper: Download a CSV blob response as a file
export const downloadCSV = async (apiCall, filename) => {
  try {
    const response = await apiCall;
    const contentType = (response.headers?.['content-type'] || '').toLowerCase();

    // If backend (or a service worker) returned JSON instead of CSV, surface the error
    // instead of saving a corrupt "CSV" file.
    if (!contentType.includes('text/csv')) {
      let detail = 'Unexpected response from server. Please try again.';
      try {
        const text =
          typeof response.data === 'string'
            ? response.data
            : await response.data.text();
        const parsed = JSON.parse(text);
        detail = parsed.detail || parsed.error || detail;
      } catch (_) {
        // ignore parse failures, keep default detail
      }
      throw new Error(detail);
    }

    const blob = new Blob([response.data], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    return true;
  } catch (error) {
    console.error('Export error:', error);
    throw error;
  }
};

// Helper: Download an authenticated blob response as a file (PDFs, images, docs).
// Use this for any per-document download where we want a consistent file save
// instead of opening in a new tab. Surfaces JSON error payloads as proper
// thrown Errors so callers can show the real reason in a toast.
export const downloadAuthenticatedFile = async (apiCall, filename) => {
  try {
    const response = await apiCall;
    const contentType = (response.headers?.['content-type'] || '').toLowerCase();

    // Detect JSON error payloads disguised as a successful blob response
    // (e.g. service-worker offline shim, misconfigured proxy).
    if (contentType.includes('application/json')) {
      let detail = 'Server returned an error.';
      try {
        const text =
          typeof response.data === 'string'
            ? response.data
            : await response.data.text();
        const parsed = JSON.parse(text);
        detail = parsed.detail || parsed.error || detail;
      } catch (_) {
        /* ignore */
      }
      throw new Error(detail);
    }

    const blob = new Blob([response.data], {
      type: contentType || 'application/octet-stream',
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 10000);
    return true;
  } catch (error) {
    console.error('Download error:', error);
    if (error?.response?.data) {
      let detail = `Server responded with ${error.response.status || 'an error'}.`;
      try {
        const text =
          typeof error.response.data === 'string'
            ? error.response.data
            : await error.response.data.text();
        const parsed = JSON.parse(text);
        detail = parsed.detail || parsed.error || detail;
      } catch (_) {
        /* ignore */
      }
      throw new Error(detail);
    }
    throw error;
  }
};

// Helper: Open an authenticated file URL in a new tab (for JWT-protected downloads)
// Uses a programmatic anchor click instead of window.open(), which most browsers
// block when invoked after an `await` (no longer in a user-gesture context).
export const openAuthenticatedFile = async (fileUrl) => {
  try {
    const path = fileUrl.startsWith('/api/') ? fileUrl.slice(4) : fileUrl;
    const response = await api.get(path, { responseType: 'blob' });

    const contentType = response.headers?.['content-type'] || 'application/octet-stream';
    // If the server returned JSON (e.g. an error payload from a misconfigured
    // service worker or auth failure), surface the message instead of opening
    // a blob that just contains the error text.
    if (contentType.toLowerCase().includes('application/json')) {
      let detail = 'Server returned an error.';
      try {
        const text = await response.data.text();
        const parsed = JSON.parse(text);
        detail = parsed.detail || parsed.error || detail;
      } catch (_) {
        /* ignore */
      }
      throw new Error(detail);
    }

    const blob = new Blob([response.data], { type: contentType });
    const blobUrl = URL.createObjectURL(blob);

    const a = document.createElement('a');
    a.href = blobUrl;
    a.target = '_blank';
    a.rel = 'noopener noreferrer';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);

    // Revoke after a delay to allow the new tab to load the resource
    setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
  } catch (error) {
    console.error('Error opening file:', error);
    if (error?.response?.status) {
      let detail = `Server responded with ${error.response.status}.`;
      try {
        const text = await error.response.data.text();
        const parsed = JSON.parse(text);
        detail = parsed.detail || parsed.error || detail;
      } catch (_) {
        /* ignore */
      }
      throw new Error(detail);
    }
    throw error;
  }
};

// Claude AI Studio API (streaming endpoints live in lib/aiStream.js)
export const aiAPI = {
  status: () => api.get('/ai/status'),
  searchRecords: (q, recordType = 'any') => api.get('/ai/records/search', { params: { q, record_type: recordType } }),
  getSessions: () => api.get('/ai/sessions'),
  getSessionMessages: (id) => api.get(`/ai/sessions/${id}/messages`),
  deleteSession: (id) => api.delete(`/ai/sessions/${id}`),
  getDrafts: () => api.get('/ai/drafts'),
  deleteDraft: (id) => api.delete(`/ai/drafts/${id}`),
};

export const leadRoutingAPI = {
  getSettings: () => api.get('/lead-routing/settings'),
  saveSettings: (data) => api.put('/lead-routing/settings', data),
  run: (limit = 50) => api.post('/lead-routing/run', { limit }),
  getLog: () => api.get('/lead-routing/log'),
};
